import { useState, useEffect, useRef, useCallback } from "react";
import { Play, Zap, Sun, Moon, ChevronDown, Copy, RotateCcw, Loader2, Check, X, ChevronsUpDown, Terminal as TermIcon, Trash2, Maximize2, Minimize2 } from "lucide-react";
import { alpha } from '../../../utils/alpha';

// ─── Font Injection ────────────────────────────────────────────────────────────
const injectFonts = () => {
  if (document.getElementById('cpg-fonts')) return;
  const l = document.createElement('link');
  l.id = 'cpg-fonts';
  l.rel = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:ital,wght@0,400;0,600;1,400&family=Syne:wght@600;700&family=DM+Sans:wght@400;500;600&display=swap';
  document.head.appendChild(l);
};

// ─── Script Loader ─────────────────────────────────────────────────────────────
const scriptCache = {};
const loadScript = (src) => {
  if (scriptCache[src]) return scriptCache[src];
  scriptCache[src] = new Promise((res, rej) => {
    const s = document.createElement('script');
    s.src = src; s.onload = res; s.onerror = rej;
    document.head.appendChild(s);
  });
  return scriptCache[src];
};

// ─── Themes ───────────────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    bg: 'var(--lyra-color-text)', panel: 'var(--lyra-color-text)', header: 'var(--lyra-color-text)',
    border: 'var(--lyra-color-text)', text: 'var(--app-surface-3)', muted: '#4a5280',
    accent: '#00f5a8', accentDim: 'rgba(0,245,168,0.08)',
    accentGlow: '0 0 20px rgba(0,245,168,0.2)',
    error: '#ff4d6a', warn: '#ffb84d', success: '#00f5a8', info: 'var(--lyra-color-primary)',
    ret: '#c084fc', editorBg: 'var(--lyra-color-text)', outputBg: 'var(--lyra-color-text)',
    termBg: 'var(--lyra-color-text)', termText: '#00f5a8', termMuted: '#2a3a2a',
    btnBg: 'linear-gradient(135deg,#00f5a8,#00b4e0)',
    btnText: '#000', scrollbar: 'var(--lyra-color-text)', caret: '#00f5a8',
    selectionBg: 'rgba(0,245,168,0.15)', highlight: 'rgba(0,245,168,0.06)',
    badgeBg: 'var(--lyra-color-text)', syntaxKw: '#c084fc', syntaxStr: '#fda4af',
    syntaxNum: 'var(--app-success-border)', syntaxBuiltin: '#67e8f9', syntaxComment: '#4a5280',
    syntaxAttr: '#93c5fd', syntaxTag: '#67e8f9', syntaxProp: '#93c5fd',
  },
  light: {
    bg: 'var(--app-surface-3)', panel: 'var(--lyra-color-surface-2)', header: 'var(--app-surface-3)',
    border: '#ddd3c0', text: 'var(--lyra-color-text)', muted: '#8a7d68',
    accent: '#b5451b', accentDim: 'rgba(181,69,27,0.07)',
    accentGlow: '0 0 20px rgba(181,69,27,0.15)',
    error: '#c0392b', warn: 'var(--lyra-color-warning)', success: 'var(--lyra-color-success)', info: 'var(--lyra-color-primary)',
    ret: 'var(--lyra-color-primary)', editorBg: 'var(--lyra-color-surface-2)', outputBg: 'var(--app-surface-3)',
    termBg: 'var(--lyra-color-text)', termText: '#a8e063', termMuted: 'var(--lyra-color-text)',
    btnBg: 'linear-gradient(135deg,#b5451b,var(--lyra-color-warning))',
    btnText: 'var(--lyra-color-surface)', scrollbar: '#ddd3c0', caret: '#b5451b',
    selectionBg: 'rgba(181,69,27,0.12)', highlight: 'rgba(181,69,27,0.04)',
    badgeBg: 'var(--app-surface-3)', syntaxKw: 'var(--lyra-color-primary)', syntaxStr: '#b5451b',
    syntaxNum: 'var(--lyra-color-success)', syntaxBuiltin: 'var(--app-primary-deep)', syntaxComment: '#8a7d68',
    syntaxAttr: 'var(--lyra-color-primary)', syntaxTag: 'var(--app-primary-deep)', syntaxProp: 'var(--lyra-color-primary)',
  }
};

// ─── Languages ────────────────────────────────────────────────────────────────
const LANGS = [
  { id: 'javascript', label: 'JavaScript', badge: 'JS', bc: '#f7df1e', bt: '#000' },
  { id: 'typescript', label: 'TypeScript', badge: 'TS', bc: '#3178c6', bt: 'var(--lyra-color-surface)' },
  { id: 'python', label: 'Python', badge: 'PY', bc: '#4B8BBE', bt: 'var(--lyra-color-surface)' },
  { id: 'html', label: 'HTML', badge: 'HT', bc: '#e34c26', bt: 'var(--lyra-color-surface)' },
  { id: 'css', label: 'CSS', badge: 'CS', bc: '#264de4', bt: 'var(--lyra-color-surface)' },
  { id: 'json', label: 'JSON', badge: '{}', bc: '#6cc24a', bt: '#000' },
];

// ─── Boilerplate ──────────────────────────────────────────────────────────────
const BOILERPLATE = {
  javascript: `// 🚀 JavaScript Playground
function fibonacci(n) {
  if (n <= 1) return n;
  return fibonacci(n - 1) + fibonacci(n - 2);
}

const sequence = Array.from({ length: 10 }, (_, i) => fibonacci(i));
console.log("Fibonacci:", sequence);

const data = [
  { name: "Alice", score: 92 },
  { name: "Bob",   score: 87 },
  { name: "Carol", score: 95 },
];

const top = data.sort((a, b) => b.score - a.score)[0];
console.log(\`Top scorer: \${top.name} with \${top.score}\`);`,

  typescript: `// 🔷 TypeScript Playground
interface User {
  name: string;
  age: number;
  roles: string[];
}

function formatUser(user: User): string {
  const roles = user.roles.join(", ");
  return \`\${user.name} (age \${user.age}) – Roles: \${roles}\`;
}

const users: User[] = [
  { name: "Alice", age: 28, roles: ["admin", "editor"] },
  { name: "Bob",   age: 34, roles: ["viewer"] },
];

users.forEach(u => console.log(formatUser(u)));`,

  python: `# 🐍 Python Playground
from typing import List

def fibonacci(n: int) -> List[int]:
    seq = [0, 1]
    for i in range(2, n):
        seq.append(seq[-1] + seq[-2])
    return seq[:n]

def greet(name: str, *, formal: bool = False) -> str:
    prefix = "Dear" if formal else "Hello"
    return f"{prefix}, {name}!"

print(fibonacci(10))
print(greet("World"))
print(greet("Sir Python", formal=True))`,

  html: `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>HTML Preview</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Segoe UI', sans-serif;
      background: linear-gradient(135deg, var(--lyra-color-text) 0%, var(--lyra-color-text) 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .card {
      background: white;
      border-radius: 16px;
      padding: 2.5rem;
      text-align: center;
      box-shadow: 0 25px 50px rgba(0,0,0,0.5);
    }
    h1 { color: var(--lyra-color-text); font-size: 2rem; margin-bottom: 0.5rem; }
    p { color: var(--app-text-2); margin-bottom: 1rem; }
    button {
      padding: 0.5rem 1.5rem;
      background: #667eea;
      color: white;
      border: none;
      border-radius: 8px;
      cursor: pointer;
      font-size: 1rem;
    }
    button:hover { background: #5a67d8; }
  </style>
</head>
<body>
  <div class="card">
    <h1>👋 Hello, World!</h1>
    <p>Edit this HTML and click Run to preview.</p>
    <button onclick="alert('Hello from HTML!')">Click me</button>
  </div>
</body>
</html>`,

  css: `/* 🎨 CSS Playground */
body {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--lyra-color-text), var(--lyra-color-text));
  font-family: 'Segoe UI', sans-serif;
  gap: 2rem;
}

.box {
  width: 160px;
  height: 160px;
  background: linear-gradient(45deg, #f093fb, #f5576c);
  border-radius: 24px;
  animation: spin 4s ease-in-out infinite;
  box-shadow: 0 0 80px rgba(240,147,251,0.5);
}

h2 { color: rgba(255,255,255,0.8); font-size: 1.2rem; letter-spacing: 0.2em; }

@keyframes spin {
  0%   { transform: rotate(0deg) scale(1); }
  50%  { transform: rotate(180deg) scale(1.15); }
  100% { transform: rotate(360deg) scale(1); }
}`,

  json: `{
  "playground": {
    "name": "Code Playground",
    "version": "2.0.0",
    "features": [
      "Syntax highlighting",
      "Multi-language support",
      "Live execution",
      "Dark & light themes",
      "Autocomplete",
      "Built-in terminal"
    ],
    "languages": {
      "browser": ["JavaScript", "TypeScript", "HTML", "CSS", "JSON"],
      "wasm": ["Python"]
    }
  }
}`
};

// ─── Keywords ─────────────────────────────────────────────────────────────────
const KEYWORDS = {
  javascript: ['function', 'const', 'let', 'var', 'return', 'if', 'else', 'for', 'while', 'do', 'switch', 'case', 'break', 'continue', 'class', 'extends', 'import', 'export', 'default', 'async', 'await', 'try', 'catch', 'finally', 'throw', 'new', 'this', 'typeof', 'instanceof', 'null', 'undefined', 'true', 'false', 'of', 'in', 'console', 'log', 'error', 'warn', 'info', 'Promise', 'Array', 'Object', 'String', 'Number', 'Boolean', 'Math', 'JSON', 'Date', 'Map', 'Set', 'fetch', 'setTimeout', 'setInterval', 'parseInt', 'parseFloat', 'isNaN', 'window', 'document', 'addEventListener'],
  typescript: ['function', 'const', 'let', 'var', 'return', 'if', 'else', 'for', 'while', 'class', 'interface', 'type', 'enum', 'extends', 'implements', 'import', 'export', 'default', 'async', 'await', 'string', 'number', 'boolean', 'void', 'any', 'never', 'unknown', 'null', 'undefined', 'true', 'false', 'readonly', 'private', 'public', 'protected', 'static', 'abstract', 'as', 'is', 'keyof', 'typeof', 'Partial', 'Required', 'Pick', 'Omit', 'Record', 'Array', 'Promise', 'Optional'],
  python: ['def', 'class', 'import', 'from', 'return', 'if', 'elif', 'else', 'for', 'while', 'in', 'not', 'and', 'or', 'is', 'try', 'except', 'finally', 'raise', 'with', 'as', 'pass', 'break', 'continue', 'lambda', 'yield', 'global', 'nonlocal', 'del', 'assert', 'True', 'False', 'None', 'print', 'len', 'range', 'list', 'dict', 'set', 'tuple', 'str', 'int', 'float', 'bool', 'type', 'isinstance', 'enumerate', 'zip', 'map', 'filter', 'sorted', 'reversed', 'open', 'input', 'super', 'self', 'append', 'extend', 'items', 'keys', 'values', 'split', 'join', 'strip', 'format', 'replace'],
  html: ['html', 'head', 'body', 'div', 'span', 'p', 'a', 'img', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'tr', 'td', 'th', 'thead', 'tbody', 'form', 'input', 'button', 'select', 'option', 'textarea', 'label', 'nav', 'header', 'footer', 'main', 'section', 'article', 'aside', 'script', 'style', 'link', 'meta', 'title', 'class', 'id', 'href', 'src', 'alt', 'type', 'name', 'value', 'placeholder', 'required', 'disabled', 'checked', 'onclick', 'onchange'],
  css: ['color', 'background', 'background-color', 'background-image', 'font-size', 'font-family', 'font-weight', 'margin', 'padding', 'border', 'border-radius', 'display', 'flex', 'grid', 'width', 'height', 'max-width', 'min-height', 'position', 'top', 'left', 'right', 'bottom', 'z-index', 'overflow', 'text-align', 'line-height', 'opacity', 'transform', 'transition', 'animation', 'box-shadow', 'justify-content', 'align-items', 'flex-direction', 'gap', 'cursor', 'visibility'],
  json: [],
};

// ─── Syntax Highlighter ───────────────────────────────────────────────────────
const esc = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

function highlight(code, lang, t) {
  const K = t.syntaxKw, S = t.syntaxStr, N = t.syntaxNum;
  const B = t.syntaxBuiltin, C = t.syntaxComment;
  const A = t.syntaxAttr, G = t.syntaxTag, P = t.syntaxProp;

  if (!code) return '';

  if (lang === 'json') {
    return esc(code)
      .replace(/("(?:[^"\\]|\\.)*")\s*:/g, `<span style="color:${G}">$1</span>:`)
      .replace(/:\s*("(?:[^"\\]|\\.)*")/g, `: <span style="color:${S}">$1</span>`)
      .replace(/\b(true|false|null)\b/g, `<span style="color:${K}">$1</span>`)
      .replace(/\b(-?\d+\.?\d*)\b/g, `<span style="color:${N}">$1</span>`);
  }

  if (lang === 'html') {
    return esc(code)
      .replace(/(&lt;!--[\s\S]*?--&gt;)/g, `<span style="color:${C};font-style:italic">$1</span>`)
      .replace(/(&lt;\/?)([\w-]+)/g, `$1<span style="color:${G}">$2</span>`)
      .replace(/([\w-]+)(?==)/g, `<span style="color:${A}">$1</span>`)
      .replace(/(".*?"|'.*?')/g, `<span style="color:${S}">$1</span>`);
  }

  if (lang === 'css') {
    return esc(code)
      .replace(/(\/\*[\s\S]*?\*\/)/g, `<span style="color:${C};font-style:italic">$1</span>`)
      .replace(/([\w-]+)\s*(?=\s*:(?!:))/g, `<span style="color:${P}">$1</span>`)
      .replace(/:\s*([^;{}\n]+)/g, `: <span style="color:${S}">$1</span>`)
      .replace(/(#[0-9a-fA-F]{3,8})\b/g, `<span style="color:${N}">$1</span>`)
      .replace(/([.#][\w-]+|:[\w-]+(?:\([^)]*\))?)/g, `<span style="color:${K}">$1</span>`);
  }

  if (lang === 'python') {
    let result = esc(code);
    const placeholders = [];
    const ph = (s) => { const i = placeholders.length; placeholders.push(s); return `\x00${i}\x00`; };

    // Comments first
    result = result.replace(/(#[^\n]*)/g, m => ph(`<span style="color:${C};font-style:italic">${m}</span>`));
    // Triple-quoted strings
    result = result.replace(/("""[\s\S]*?"""|'''[\s\S]*?''')/g, m => ph(`<span style="color:${S}">${m}</span>`));
    // Strings
    result = result.replace(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g, m => ph(`<span style="color:${S}">${m}</span>`));
    // Decorators
    result = result.replace(/@[\w.]+/g, m => ph(`<span style="color:${K}">${m}</span>`));
    // Keywords
    result = result.replace(/\b(def|class|import|from|return|if|elif|else|for|while|in|not|and|or|is|try|except|finally|raise|with|as|pass|break|continue|lambda|yield|global|nonlocal|del|assert)\b/g, m => ph(`<span style="color:${K};font-weight:bold">${m}</span>`));
    result = result.replace(/\b(True|False|None)\b/g, `<span style="color:${N}">$1</span>`);
    result = result.replace(/\b(print|len|range|list|dict|set|tuple|str|int|float|bool|type|isinstance|enumerate|zip|map|filter|sorted|reversed|open|input|super|self)\b/g, `<span style="color:${B}">$1</span>`);
    result = result.replace(/\b(\d+\.?\d*)\b/g, `<span style="color:${N}">$1</span>`);

    placeholders.forEach((p, i) => { result = result.replace(`\x00${i}\x00`, p); });
    return result;
  }

  // JS / TS — use full placeholder system to prevent number regex matching inside span attrs
  let result = esc(code);
  const placeholders = [];
  const ph = (s) => { const i = placeholders.length; placeholders.push(s); return `\x00${i}\x00`; };

  // 1. Comments & strings first (protect their content)
  result = result.replace(/(\/\/[^\n]*)/g, m => ph(`<span style="color:${C};font-style:italic">${m}</span>`));
  result = result.replace(/(\/\*[\s\S]*?\*\/)/g, m => ph(`<span style="color:${C};font-style:italic">${m}</span>`));
  result = result.replace(/(`(?:[^`\\]|\\.)*`)/g, m => ph(`<span style="color:${S}">${m}</span>`));
  result = result.replace(/("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g, m => ph(`<span style="color:${S}">${m}</span>`));

  // 2. Numbers BEFORE keywords — avoids matching inside future span attrs
  result = result.replace(/\b(\d+\.?\d*)\b/g, m => ph(`<span style="color:${N}">${m}</span>`));

  // 3. Keywords (use font-weight:bold not 600 to avoid any numeric leakage)
  result = result.replace(/\b(function|const|let|var|return|if|else|for|while|do|switch|case|break|continue|class|extends|import|export|default|async|await|try|catch|finally|throw|new|typeof|instanceof|of|in|delete|void|interface|type|enum|implements|readonly|private|public|protected|static|abstract|as|is|keyof)\b/g, m => ph(`<span style="color:${K};font-weight:bold">${m}</span>`));
  result = result.replace(/\b(true|false|null|undefined|this|string|number|boolean|any|never|unknown)\b/g, m => ph(`<span style="color:${N}">${m}</span>`));
  result = result.replace(/\b(console|Math|JSON|Array|Object|String|Number|Boolean|Promise|Date|Map|Set|window|document|fetch|setTimeout|setInterval|Partial|Required|Pick|Omit|Record)\b/g, m => ph(`<span style="color:${B}">${m}</span>`));

  placeholders.forEach((p, i) => { result = result.replace(`\x00${i}\x00`, p); });
  return result;
}

// ─── Prettify ─────────────────────────────────────────────────────────────────
function prettify(code, lang) {
  try {
    if (lang === 'json') return JSON.stringify(JSON.parse(code), null, 2);
    if (lang === 'javascript' || lang === 'typescript') return prettifyJS(code);
    if (lang === 'html') return prettifyHTML(code);
    if (lang === 'css') return prettifyCSS(code);
  } catch { }
  return code;
}

function prettifyJS(code) {
  const lines = code.split('\n');
  let indent = 0;
  return lines.map(raw => {
    const line = raw.trim();
    if (!line) return '';
    const opens = (line.match(/[{[(]/g) || []).length;
    const closes = (line.match(/[}\])]/g) || []).length;
    if (line.match(/^[}\])]/)) indent = Math.max(0, indent - 1);
    const out = '  '.repeat(indent) + line;
    indent = Math.max(0, indent + opens - closes + (line.match(/^[}\])]/) ? 1 : 0));
    return out;
  }).join('\n');
}

function prettifyHTML(code) {
  const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
  let indent = 0;
  return code.replace(/>\s*</g, '>\n<').split('\n').map(raw => {
    const line = raw.trim();
    if (!line) return '';
    const close = line.match(/^<\/([\w-]+)/);
    const open = line.match(/^<([\w-]+)/);
    if (close) indent = Math.max(0, indent - 1);
    const out = '  '.repeat(indent) + line;
    if (open && !VOID.has(open[1].toLowerCase()) && !line.includes('/>') && !line.includes(`</${open[1]}`)) indent++;
    return out;
  }).filter(Boolean).join('\n');
}

function prettifyCSS(code) {
  return code
    .replace(/\s*\{\s*/g, ' {\n  ').replace(/;\s*/g, ';\n  ')
    .replace(/\s*\}\s*/g, '\n}\n').replace(/\s*,\s*(?=[^{]*\{)/g, ',\n')
    .split('\n').map(l => l.trimEnd()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

// ─── Code Runner ──────────────────────────────────────────────────────────────
function fmtArg(a) {
  if (typeof a === 'object' && a !== null) { try { return JSON.stringify(a, null, 2); } catch { return String(a); } }
  return String(a);
}

function runJS(code) {
  const logs = [];
  const _l = console.log, _e = console.error, _w = console.warn, _i = console.info;
  console.log = (...a) => { _l(...a); logs.push({ type: 'log', text: a.map(fmtArg).join(' ') }); };
  console.error = (...a) => { _e(...a); logs.push({ type: 'error', text: a.map(fmtArg).join(' ') }); };
  console.warn = (...a) => { _w(...a); logs.push({ type: 'warn', text: a.map(fmtArg).join(' ') }); };
  console.info = (...a) => { _i(...a); logs.push({ type: 'info', text: a.map(fmtArg).join(' ') }); };
  try {
    const r = new Function(code)();
    if (r !== undefined) logs.push({ type: 'ret', text: `→ ${fmtArg(r)}` });
  } catch (e) {
    logs.push({ type: 'error', text: e.toString() });
  } finally {
    console.log = _l; console.error = _e; console.warn = _w; console.info = _i;
  }
  return { type: 'console', logs };
}

async function executeCode(code, lang, pyRef) {
  if (lang === 'html') return { type: 'html', content: code };
  if (lang === 'css') return { type: 'html', content: `<!DOCTYPE html><html><head><style>${code}</style></head><body><div class="box"></div><h2>CSS Preview</h2></body></html>` };
  if (lang === 'json') { try { return { type: 'json', content: JSON.stringify(JSON.parse(code), null, 2) }; } catch (e) { return { type: 'error', content: `JSON Error: ${e.message}` }; } }
  if (lang === 'javascript') return runJS(code);
  if (lang === 'typescript') {
    try {
      if (!window.Babel) await loadScript('https://cdnjs.cloudflare.com/ajax/libs/babel-standalone/7.23.5/babel.min.js');
      const out = window.Babel.transform(code, { presets: ['typescript'], filename: 'file.ts' }).code;
      return runJS(out);
    } catch (e) { return { type: 'error', content: `TypeScript Error: ${e.message}` }; }
  }
  if (lang === 'python') {
    try {
      if (!window.loadPyodide) await loadScript('https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js');
      if (!pyRef.current) {
        pyRef.current = await window.loadPyodide({ indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/' });
      }
      const py = pyRef.current;
      py.runPython('import sys,io; _buf=io.StringIO(); sys.stdout=_buf');
      try {
        py.runPython(code);
        const out = py.runPython('_buf.getvalue()');
        return { type: 'console', logs: [{ type: 'log', text: out || '(no output)' }] };
      } catch (e) {
        return { type: 'error', content: e.message };
      } finally {
        py.runPython('sys.stdout=sys.__stdout__');
      }
    } catch (e) { return { type: 'error', content: `Python error: ${e.message}` }; }
  }
  return { type: 'error', content: 'Language not supported' };
}

// ─── Terminal Commands ────────────────────────────────────────────────────────
const TERM_COMMANDS = {
  help: () => `Available commands:\n  help        – Show this help\n  clear       – Clear terminal\n  ls          – List files\n  echo [text] – Print text\n  date        – Show current date\n  node -v     – Node.js version\n  python -V   – Python version\n  run         – Run current code`,
  ls: () => `playground/\n  ├── index.js\n  ├── main.ts\n  ├── app.py\n  └── index.html`,
  date: () => new Date().toLocaleString(),
  'node -v': () => 'v22.9.0',
  'node --version': () => 'v22.9.0',
  'python -V': () => 'Python 3.11.4',
  'python --version': () => 'Python 3.11.4',
  'python3 -V': () => 'Python 3.11.4',
  'tsc --version': () => 'Version 5.4.5',
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CodePlayground() {
  const [theme, setTheme] = useState('dark');
  const [lang, setLang] = useState('javascript');
  const [code, setCode] = useState(BOILERPLATE.javascript);
  const [output, setOutput] = useState(null);
  const [running, setRunning] = useState(false);
  const [loadingPy, setLoadingPy] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [termOpen, setTermOpen] = useState(true);
  const [termLines, setTermLines] = useState([{ type: 'system', text: 'Code Playground Terminal v2.0  •  type help for commands' }]);
  const [termInput, setTermInput] = useState('');
  const [termHistory, setTermHistory] = useState([]);
  const [termHistIdx, setTermHistIdx] = useState(-1);
  const [acSuggestions, setAcSuggestions] = useState([]);
  const [acIdx, setAcIdx] = useState(0);
  const [acWord, setAcWord] = useState('');
  const [prettified, setPrettified] = useState(false);

  const pyRef = useRef(null);
  const textareaRef = useRef(null);
  const preRef = useRef(null);
  const termEndRef = useRef(null);
  const termInputRef = useRef(null);
  const outputFrameRef = useRef(null);

  const t = THEMES[theme];

  useEffect(() => { injectFonts(); }, []);
  useEffect(() => { termEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [termLines]);

  // Switch language
  const switchLang = (id) => {
    setLang(id);
    setCode(BOILERPLATE[id]);
    setOutput(null);
    setLangOpen(false);
    setAcSuggestions([]);
    // Warm up Pyodide
    if (id === 'python') {
      setLoadingPy(true);
      Promise.resolve().then(async () => {
        try {
          if (!window.loadPyodide) await loadScript('https://cdn.jsdelivr.net/pyodide/v0.25.0/full/pyodide.js');
          if (!pyRef.current) pyRef.current = await window.loadPyodide({ indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.25.0/full/' });
        } catch { }
        setLoadingPy(false);
      });
    }
  };

  // Sync scroll between textarea and pre
  const syncScroll = () => {
    if (preRef.current && textareaRef.current) {
      preRef.current.scrollTop = textareaRef.current.scrollTop;
      preRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  // Autocomplete logic
  const getWordAtCursor = (text, pos) => {
    const before = text.slice(0, pos);
    const match = before.match(/[\w-]+$/);
    return match ? match[0] : '';
  };

  const handleCodeChange = (e) => {
    const val = e.target.value;
    setCode(val);
    setPrettified(false);
    const cursor = e.target.selectionStart;
    const word = getWordAtCursor(val, cursor);
    if (word.length >= 2) {
      const kw = KEYWORDS[lang] || [];
      const matches = kw.filter(k => k.startsWith(word) && k !== word).slice(0, 6);
      setAcSuggestions(matches);
      setAcWord(word);
      setAcIdx(0);
    } else {
      setAcSuggestions([]);
    }
  };

  const acceptSuggestion = (suggestion) => {
    const ta = textareaRef.current;
    const pos = ta.selectionStart;
    const before = code.slice(0, pos);
    const after = code.slice(pos);
    const start = before.lastIndexOf(acWord);
    const newCode = code.slice(0, start) + suggestion + after;
    setCode(newCode);
    setAcSuggestions([]);
    setTimeout(() => {
      ta.selectionStart = ta.selectionEnd = start + suggestion.length;
      ta.focus();
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (acSuggestions.length > 0) {
      if (e.key === 'Tab' || e.key === 'Enter') {
        if (acSuggestions[acIdx]) { e.preventDefault(); acceptSuggestion(acSuggestions[acIdx]); return; }
      }
      if (e.key === 'ArrowDown') { e.preventDefault(); setAcIdx(i => Math.min(i + 1, acSuggestions.length - 1)); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAcIdx(i => Math.max(i - 1, 0)); return; }
      if (e.key === 'Escape') { setAcSuggestions([]); return; }
    }
    // Tab = 2 spaces
    if (e.key === 'Tab') {
      e.preventDefault();
      const ta = textareaRef.current;
      const s = ta.selectionStart, end = ta.selectionEnd;
      const newCode = code.slice(0, s) + '  ' + code.slice(end);
      setCode(newCode);
      setTimeout(() => { ta.selectionStart = ta.selectionEnd = s + 2; }, 0);
    }
  };

  // Run code
  const run = async () => {
    setRunning(true);
    setOutput(null);
    setAcSuggestions([]);
    const start = Date.now();
    try {
      const result = await executeCode(code, lang, pyRef);
      const ms = Date.now() - start;
      setOutput({ ...result, ms });
      const summary = result.type === 'error' ? `Error (${ms}ms)` : `Done (${ms}ms)`;
      setTermLines(prev => [...prev,
      { type: 'cmd', text: `> run ${lang}` },
      { type: result.type === 'error' ? 'error' : 'success', text: summary },
      ...(result.type === 'console' ? result.logs.map(l => ({ type: l.type, text: l.text })) : []),
      ...(result.type === 'error' ? [{ type: 'error', text: result.content }] : []),
      ]);
    } catch (e) {
      setOutput({ type: 'error', content: e.message });
    }
    setRunning(false);
  };

  // Prettify
  const handlePrettify = () => {
    const p = prettify(code, lang);
    setCode(p);
    setPrettified(true);
    setTimeout(() => setPrettified(false), 1500);
  };

  // Copy
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Reset
  const handleReset = () => { setCode(BOILERPLATE[lang]); setOutput(null); setAcSuggestions([]); };

  // Terminal
  const handleTermCmd = (cmd) => {
    const trimmed = cmd.trim().toLowerCase();
    setTermLines(prev => [...prev, { type: 'cmd', text: `$ ${cmd}` }]);
    if (!trimmed) return;
    setTermHistory(h => [cmd, ...h.slice(0, 49)]);
    setTermHistIdx(-1);
    if (trimmed === 'clear') { setTermLines([]); return; }
    if (trimmed === 'run') { run(); return; }
    const handler = TERM_COMMANDS[trimmed];
    if (handler) {
      setTermLines(prev => [...prev, { type: 'output', text: handler() }]);
    } else {
      setTermLines(prev => [...prev, { type: 'error', text: `command not found: ${cmd}` }]);
    }
  };

  const handleTermKey = (e) => {
    if (e.key === 'Enter') { handleTermCmd(termInput); setTermInput(''); }
    if (e.key === 'ArrowUp') { const i = Math.min(termHistIdx + 1, termHistory.length - 1); setTermHistIdx(i); setTermInput(termHistory[i] || ''); }
    if (e.key === 'ArrowDown') { const i = Math.max(termHistIdx - 1, -1); setTermHistIdx(i); setTermInput(i < 0 ? '' : termHistory[i] || ''); }
  };

  const langInfo = LANGS.find(l => l.id === lang);

  // ─── Render ───────────────────────────────────────────────────────────────
  const monoFont = '"JetBrains Mono", "Fira Code", monospace';
  const uiFont = '"DM Sans", "Segoe UI", sans-serif';

  const termColor = (type) => {
    if (type === 'cmd') return t.termText;
    if (type === 'error') return '#ff4d6a';
    if (type === 'warn') return '#ffb84d';
    if (type === 'success') return 'var(--app-success-border)';
    if (type === 'system') return 'var(--lyra-color-text-muted)';
    if (type === 'ret') return t.theme === 'dark' ? '#c084fc' : 'var(--lyra-color-primary)';
    return 'var(--app-border-strong)';
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
      background: t.bg, color: t.text, fontFamily: uiFont,
    }}>
      {/* ── HEADER ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
        background: t.header, borderBottom: `1px solid ${t.border}`,
        flexShrink: 0, zIndex: 10,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '8px' }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8, background: t.accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: t.accentGlow,
          }}>
            <span style={{ fontSize: '16px', lineHeight: 1 }}>⌨</span>
          </div>
          <span style={{ fontFamily: '"Syne", sans-serif', fontWeight: 700, fontSize: '15px', letterSpacing: '0.02em' }}>
            <span style={{ color: t.accent }}>Code</span>Playground
          </span>
        </div>

        {/* Language Selector */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setLangOpen(o => !o)} style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            background: t.panel, border: `1px solid ${t.border}`,
            borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
            color: t.text, fontFamily: uiFont, fontSize: '13px',
          }}>
            <span style={{
              background: langInfo.bc, color: langInfo.bt,
              borderRadius: 4, padding: '1px 6px', fontSize: '11px', fontWeight: 700,
              fontFamily: monoFont,
            }}>{langInfo.badge}</span>
            <span style={{ fontWeight: 500 }}>{langInfo.label}</span>
            {loadingPy && <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: t.accent }} />}
            <ChevronDown size={13} style={{ color: t.muted }} />
          </button>
          {langOpen && (
            <div style={{
              position: 'absolute', top: '110%', left: 0, zIndex: 100,
              background: t.panel, border: `1px solid ${t.border}`,
              borderRadius: 10, overflow: 'hidden', minWidth: 160,
              boxShadow: `0 8px 32px rgba(0,0,0,0.4)`,
            }}>
              {LANGS.map(l => (
                <button key={l.id} onClick={() => switchLang(l.id)} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  width: '100%', padding: '8px 14px', background: l.id === lang ? t.accentDim : 'transparent',
                  border: 'none', cursor: 'pointer', color: t.text, fontFamily: uiFont, fontSize: '13px',
                  textAlign: 'left',
                }}>
                  <span style={{
                    background: l.bc, color: l.bt, borderRadius: 4,
                    padding: '1px 6px', fontSize: '11px', fontWeight: 700, fontFamily: monoFont,
                  }}>{l.badge}</span>
                  {l.label}
                  {l.id === lang && <Check size={13} style={{ marginLeft: 'auto', color: t.accent }} />}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Prettify */}
        <button onClick={handlePrettify} title="Prettify / Format code" style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: prettified ? t.accentDim : t.panel,
          border: `1px solid ${prettified ? t.accent : t.border}`,
          borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
          color: prettified ? t.accent : t.text, fontFamily: uiFont, fontSize: '13px',
          transition: 'all 0.2s',
        }}>
          <Zap size={13} style={{ color: prettified ? t.accent : t.muted }} />
          {prettified ? 'Prettified!' : 'Prettify'}
        </button>

        {/* Copy */}
        <button onClick={handleCopy} title="Copy code" style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: t.panel, border: `1px solid ${t.border}`,
          borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: t.muted,
        }}>
          {copied ? <Check size={13} style={{ color: t.success }} /> : <Copy size={13} />}
        </button>

        {/* Reset */}
        <button onClick={handleReset} title="Reset to default" style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: t.panel, border: `1px solid ${t.border}`,
          borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: t.muted,
        }}>
          <RotateCcw size={13} />
        </button>

        <div style={{ flex: 1 }} />

        {/* Theme Toggle */}
        <button onClick={() => setTheme(th => th === 'dark' ? 'light' : 'dark')} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: t.panel, border: `1px solid ${t.border}`,
          borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
          color: t.text, fontFamily: uiFont, fontSize: '13px',
        }}>
          {theme === 'dark' ? <Sun size={14} style={{ color: '#fbbf24' }} /> : <Moon size={14} style={{ color: '#818cf8' }} />}
          {theme === 'dark' ? 'Light' : 'Dark'}
        </button>
      </div>

      {/* ── MAIN ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {/* ── LEFT: Editor ── */}
        <div style={{
          flex: '0 0 55%', display: 'flex', flexDirection: 'column',
          borderRight: `1px solid ${t.border}`, minWidth: 0, overflow: 'hidden',
        }}>
          {/* Editor bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '6px 12px', background: t.panel, borderBottom: `1px solid ${t.border}`,
            fontSize: '12px', color: t.muted, flexShrink: 0,
          }}>
            <span style={{ fontFamily: monoFont }}>
              {lang === 'javascript' ? 'index.js' :
                lang === 'typescript' ? 'main.ts' :
                  lang === 'python' ? 'app.py' :
                    lang === 'html' ? 'index.html' :
                      lang === 'css' ? 'styles.css' : 'data.json'}
            </span>
            <span style={{ marginLeft: 'auto' }}>{code.split('\n').length} lines</span>
          </div>

          {/* Code Editor with Overlay Highlighting */}
          <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
            {/* Pre overlay: overflow:hidden (scrolled via syncScroll), box-sizing:border-box
                so padding is included in inset:0 bounds — must match textarea exactly */}
            <pre ref={preRef} aria-hidden="true" style={{
              position: 'absolute', inset: 0, margin: 0,
              padding: '16px', fontFamily: monoFont, fontSize: '13.5px',
              lineHeight: '1.65', tabSize: 2, pointerEvents: 'none',
              overflow: 'hidden', whiteSpace: 'pre',
              wordWrap: 'normal', overflowWrap: 'normal', wordBreak: 'normal',
              boxSizing: 'border-box',
              background: t.editorBg, color: t.text, zIndex: 1,
            }} dangerouslySetInnerHTML={{ __html: highlight(code, lang, t) + '\n' }} />

            {/* Textarea: inset:0 already fills container — no width/height:100% (avoids
                double-padding with content-box). box-sizing:border-box matches pre exactly. */}
            <textarea ref={textareaRef}
              value={code}
              onChange={handleCodeChange}
              onKeyDown={handleKeyDown}
              onScroll={syncScroll}
              spellCheck={false} autoComplete="off" autoCorrect="off" autoCapitalize="off"
              style={{
                position: 'absolute', inset: 0,
                padding: '16px', fontFamily: monoFont, fontSize: '13.5px',
                lineHeight: '1.65', tabSize: 2,
                overflow: 'auto', whiteSpace: 'pre',
                wordWrap: 'normal', overflowWrap: 'normal', wordBreak: 'normal',
                boxSizing: 'border-box',
                background: 'transparent', border: 'none', outline: 'none',
                resize: 'none', color: 'transparent',
                caretColor: t.caret, zIndex: 2,
              }}
            />

            {/* Autocomplete dropdown */}
            {acSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', left: '16px', bottom: '16px', zIndex: 20,
                background: t.panel, border: `1px solid ${t.accent}`,
                borderRadius: 8, overflow: 'hidden',
                boxShadow: `0 8px 32px rgba(0,0,0,0.5), ${t.accentGlow}`,
                minWidth: 180,
              }}>
                <div style={{ padding: '4px 10px', borderBottom: `1px solid ${t.border}`, fontSize: '10px', color: t.muted, fontFamily: monoFont }}>
                  ↑↓ navigate · Tab/⏎ accept · Esc dismiss
                </div>
                {acSuggestions.map((s, i) => (
                  <button key={s} onClick={() => acceptSuggestion(s)} style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '6px 12px', fontFamily: monoFont, fontSize: '13px',
                    background: i === acIdx ? t.accentDim : 'transparent',
                    border: 'none', cursor: 'pointer',
                    color: i === acIdx ? t.accent : t.text,
                    borderLeft: i === acIdx ? `2px solid ${t.accent}` : '2px solid transparent',
                  }}>
                    <span style={{ color: t.accent }}>{s.slice(0, acWord.length)}</span>
                    {s.slice(acWord.length)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Output ── */}
        <div style={{ flex: '0 0 45%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* Run bar */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '8px 16px', background: t.panel, borderBottom: `1px solid ${t.border}`,
            flexShrink: 0,
          }}>
            <button onClick={run} disabled={running} style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: running ? t.border : t.btnBg,
              color: running ? t.muted : t.btnText,
              border: 'none', borderRadius: 8, padding: '7px 20px',
              cursor: running ? 'not-allowed' : 'pointer',
              fontFamily: uiFont, fontSize: '13px', fontWeight: 600,
              boxShadow: running ? 'none' : t.accentGlow,
              transition: 'all 0.2s',
            }}>
              {running
                ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Running…</>
                : <><Play size={14} fill="currentColor" /> Run</>
              }
            </button>

            {output && (
              <span style={{ fontSize: '12px', color: output.type === 'error' ? t.error : t.success, fontFamily: monoFont }}>
                {output.type === 'error' ? '✗ Error' : '✓ Done'} {output.ms ? `(${output.ms}ms)` : ''}
              </span>
            )}

            <div style={{ flex: 1 }} />
            <span style={{ fontSize: '11px', color: t.muted }}>Output</span>
          </div>

          {/* Output panel */}
          <div style={{ flex: 1, overflow: 'auto', background: t.outputBg, position: 'relative' }}>
            {!output && !running && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', color: t.muted, gap: '12px',
              }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  border: `2px dashed ${t.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '24px',
                }}>▷</div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: 4 }}>Ready to run</div>
                  <div style={{ fontSize: '12px', color: t.muted }}>Press Run or Ctrl+Enter</div>
                </div>
              </div>
            )}

            {running && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12 }}>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: t.accent }} />
                <span style={{ color: t.muted, fontSize: '14px' }}>
                  {lang === 'python' && !pyRef.current ? 'Loading Python (Pyodide)…' : 'Executing…'}
                </span>
              </div>
            )}

            {output && !running && output.type === 'html' && (
              <iframe
                ref={outputFrameRef}
                srcDoc={output.content}
                title="Output"
                sandbox="allow-scripts allow-same-origin"
                style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
              />
            )}

            {output && !running && output.type === 'json' && (
              <pre style={{
                padding: '16px', margin: 0, fontFamily: monoFont,
                fontSize: '13px', lineHeight: '1.65', color: t.text,
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {output.content}
              </pre>
            )}

            {output && !running && output.type === 'console' && (
              <div style={{ padding: '12px', fontFamily: monoFont, fontSize: '13px', lineHeight: '1.8' }}>
                {output.logs.map((log, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: '8px', padding: '2px 0',
                    borderBottom: i < output.logs.length - 1 ? `1px solid ${alpha(t.border, '22')}` : 'none',
                  }}>
                    <span style={{
                      color: log.type === 'error' ? t.error :
                        log.type === 'warn' ? t.warn :
                          log.type === 'info' ? t.info :
                            log.type === 'ret' ? '#c084fc' : t.muted,
                      fontSize: '10px', minWidth: '36px', textAlign: 'right',
                      paddingTop: '2px', userSelect: 'none',
                    }}>
                      {log.type === 'error' ? 'ERR' : log.type === 'warn' ? 'WRN' : log.type === 'info' ? 'INF' : log.type === 'ret' ? 'RTN' : 'LOG'}
                    </span>
                    <span style={{
                      color: log.type === 'error' ? t.error :
                        log.type === 'warn' ? t.warn :
                          log.type === 'ret' ? '#c084fc' : t.text,
                      whiteSpace: 'pre-wrap', wordBreak: 'break-word', flex: 1,
                    }}>{log.text}</span>
                  </div>
                ))}
              </div>
            )}

            {output && !running && output.type === 'error' && (
              <div style={{ padding: '16px' }}>
                <div style={{
                  background: 'rgba(255,77,106,0.1)', border: `1px solid ${alpha(t.error, '44')}`,
                  borderRadius: 10, padding: '16px',
                }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <X size={16} style={{ color: t.error, flexShrink: 0, marginTop: 2 }} />
                    <pre style={{ margin: 0, fontFamily: monoFont, fontSize: '13px', color: t.error, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {output.content}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── TERMINAL ── */}
      <div style={{
        flexShrink: 0, borderTop: `1px solid ${t.border}`,
        display: 'flex', flexDirection: 'column',
        height: termOpen ? '220px' : '36px',
        transition: 'height 0.2s ease',
        background: t.termBg,
      }}>
        {/* Terminal header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '0 12px', height: 36, flexShrink: 0,
          borderBottom: termOpen ? `1px solid ${t.termMuted}` : 'none',
          cursor: 'pointer',
        }} onClick={() => setTermOpen(o => !o)}>
          <div style={{ display: 'flex', gap: '5px' }}>
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#ff5f57' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#febc2e' }} />
            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#28c840' }} />
          </div>
          <TermIcon size={13} style={{ color: t.termText }} />
          <span style={{ fontSize: '12px', color: t.termText, fontFamily: monoFont, fontWeight: 600 }}>
            Terminal
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={(e) => { e.stopPropagation(); setTermLines([]); }} title="Clear" style={{
            background: 'none', border: 'none', cursor: 'pointer', color: t.termText, padding: '2px', opacity: 0.6,
          }}>
            <Trash2 size={12} />
          </button>
          {termOpen ? <Minimize2 size={12} style={{ color: t.termText, opacity: 0.6 }} /> : <Maximize2 size={12} style={{ color: t.termText, opacity: 0.6 }} />}
        </div>

        {termOpen && (
          <>
            {/* Output */}
            <div style={{ flex: 1, overflow: 'auto', padding: '8px 14px', fontFamily: monoFont, fontSize: '12.5px', lineHeight: '1.6' }}>
              {termLines.map((line, i) => (
                <div key={i} style={{
                  color: line.type === 'cmd' ? t.termText :
                    line.type === 'error' ? '#ff4d6a' :
                      line.type === 'success' ? 'var(--app-success-border)' :
                        line.type === 'warn' ? '#fbbf24' :
                          line.type === 'system' ? 'var(--lyra-color-text-muted)' :
                            '#b0b8d0',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {line.text}
                </div>
              ))}
              <div ref={termEndRef} />
            </div>

            {/* Input */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderTop: `1px solid ${t.termMuted}`, flexShrink: 0 }}>
              <span style={{ color: t.termText, fontFamily: monoFont, fontSize: '12.5px', userSelect: 'none' }}>$</span>
              <input
                ref={termInputRef}
                value={termInput}
                onChange={e => setTermInput(e.target.value)}
                onKeyDown={handleTermKey}
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  color: t.termText, fontFamily: monoFont, fontSize: '12.5px',
                  caretColor: t.termText,
                }}
                placeholder="type a command…"
                autoComplete="off" spellCheck={false}
              />
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        textarea::selection { background: ${t.selectionBg}; }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${t.scrollbar}; border-radius: 3px; }
      `}</style>
    </div>
  );
}
